// src/services/user.service.ts
import axios from 'axios';
import authHeader from './auth-header';
import { API_URL } from '../config/api.config';
import { User, UserListResponse } from '../interfaces/User';

// Get all users with pagination and optional search
const getUsers = async (search: string = '', page: number = 1, limit: number = 10): Promise<UserListResponse> => {
  try {
    console.log('Fetching users:', { search, page, limit });
    console.log('API URL:', `${API_URL}/users`);
    console.log('Auth headers:', authHeader());
    
    // Special case for -1: get all users with a high limit
    const actualLimit = limit === -1 ? 1000 : limit;
    
    const response = await axios.get(`${API_URL}/users`, { 
      headers: authHeader(),
      params: { search, page, limit: actualLimit }
    });
    
    console.log('User data response:', response.data);
    // If the API returns valid data, return it directly
    if (response.data && response.data.users) {
      return response.data;
    } else if (response.data) {
      // If API returns data but in unexpected format, try to adapt it
      console.warn('API returned data in unexpected format, attempting to adapt');
      const users = response.data.data || response.data.results || response.data.items || [];
      return {
        users: Array.isArray(users) ? users : [],
        currentPage: response.data.currentPage || response.data.page || page,
        totalPages: response.data.totalPages || response.data.pages || 1
      };
    }
    // If API returns completely empty or malformed data
    console.warn('API returned invalid data format');
    throw new Error('Invalid data format received from API');
  } catch (error: any) {
    console.error('Error fetching users:', error);
    // Return empty user list on error
    return {
      users: [],
      currentPage: page,
      totalPages: 1
    };
  }
};

// Get user details by ID
const getUserDetails = async (userId: number): Promise<User> => {
  try {
    const response = await axios.get(`${API_URL}/users/${userId}`, { headers: authHeader() });
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { message: 'Failed to fetch user details' };
  }
};

// Update user's role
const updateUserRole = async (userId: number, roleId: number): Promise<User> => {
  try {
    console.log(`Updating user ${userId} role to ${roleId}`);
    console.log('API URL:', `${API_URL}/users/${userId}/role`);
    
    // Log complete auth headers for debugging
    const headers = authHeader();
    console.log('Auth headers:', headers);
    
    if (!headers.Authorization) {
      console.error('No auth token available');
      throw new Error('Authentication required. Please log in again.');
    }
    
    console.log('Request body:', { role_id: roleId });
    
    try {
      const response = await axios.patch(
        `${API_URL}/users/${userId}/role`, 
        { role_id: roleId }, 
        { headers }
      );
      
      console.log('Role update response:', response.data);
      return response.data;
    } catch (apiError: any) {
      // Log detailed error information
      console.error('API Error:', apiError);
      console.error('Error details:', {
        status: apiError.response?.status,
        statusText: apiError.response?.statusText,
        data: apiError.response?.data
      });
      
      // Throw meaningful error
      throw apiError.response?.data || { message: 'Failed to update user role' };
    }
  } catch (error: any) {
    console.error('Error updating user role:', error);
    throw error;
  }
};

// Update user profile information
const updateUser = async (userId: number, userData: any) => {
  try {
    const response = await axios.patch(
      `${API_URL}/users/${userId}`,
      userData, 
      { headers: authHeader() }
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { message: 'Failed to update user' };
  }
};

// Create a new user with role
const createUser = async (userData: {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  phone: string;
  role_id: number;
}): Promise<User> => {
  try {
    const response = await axios.post(
      `${API_URL}/auth/register`, 
      userData, 
      { headers: authHeader() }
    );
    return response.data;
  } catch (error: any) {
    console.error('Error creating user:', error);
    throw error.response?.data || { message: 'Failed to create user' };
  }
};

const UserService = {
  getUsers,
  getUserDetails,
  updateUserRole,
  updateUser,
  createUser
};

export default UserService;
